import React, { useState, useRef } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { InputGroup as InputGroupBs } from '@bootstrap-styled/v4'
import { transition } from '@bootstrap-styled/css-mixins'
import cn from 'classnames'

// type definition
export type InputGroupProps = {} & React.ComponentProps<typeof InputGroupBs>

const InputGroupUnstyled: React.FC<InputGroupProps> = ({ children, className }) => {
    const [focused, setFocused] = useState(false)
    const [empty, setEmpty] = useState(true)
    const inputRef = useRef<HTMLInputElement>(null)

    // wrap coming click event handler
    const makeHandleClick = (passHandler?: React.MouseEventHandler) => (
        event: React.MouseEvent
    ): void => {
        if (inputRef && inputRef.current) {
            if (inputRef.current instanceof HTMLInputElement) {
                inputRef.current.focus()
            } else {
                // eslint-disable-next-line react/no-find-dom-node
                ;(ReactDOM.findDOMNode(inputRef.current) as HTMLInputElement).focus()
            }
        }

        if (typeof passHandler === 'function') {
            passHandler(event)
        }
    }

    // wrap coming focus and blur event handler
    const makeHandleFocus = (passHandler?: React.FocusEventHandler) => (
        event: React.FocusEvent
    ): void => {
        if (event.type === 'focus') {
            setFocused(true)
        } else {
            setFocused(false)
        }

        if (typeof passHandler === 'function') {
            passHandler(event)
        }
    }

    // wrap coming change event to validate if value is empty
    const makeHandleChange = (passHandler?: React.ChangeEventHandler<HTMLInputElement>) => (
        event
    ): void => {
        const target = event.target || event.currentTarget
        setEmpty(!target.value)

        if (typeof passHandler === 'function') {
            passHandler(event)
        }
    }

    // passes new props and checks input
    const newChildren = React.Children.map<React.ReactNode, React.ReactNode>(children, child => {
        if (typeof child === 'object' && child !== null && 'props' in child) {
            const newProps = {
                onFocus: makeHandleFocus(child.props.onFocus),
                onBlur: makeHandleFocus(child.props.onBlur),
                onClick: makeHandleClick(child.props.onClick)
            }

            if (
                Object.hasOwnProperty.call(child.props, 'type') ||
                (typeof window !== 'undefined' &&
                    (child instanceof HTMLInputElement || child instanceof HTMLSelectElement))
            ) {
                newProps['onChange'] = makeHandleChange(child.props.onChange)
                newProps['ref'] = inputRef
            }

            return React.cloneElement(child, newProps)
        }

        return child
    })

    const classes = cn(className, { 'input-group-focused': focused, 'input-group-filled': !empty })

    return <InputGroupBs className={classes}>{newChildren}</InputGroupBs>
}

const InputGroup = styled(InputGroupUnstyled)`
    &.input-group {
        .input-group-addon {
            ${props =>
                transition(props.theme['$enable-transitions'], props.theme['$input-transition'])};
            padding-top: 0;
            padding-bottom: 0;
        }

        /* input was focused */
        &.input-group-focused {
            .input-group-addon {
                border: ${props => props.theme['$input-border-width']} solid
                    ${props => props.theme['$input-focus-border-color']};

                &:not(:first-child):not(:last-child) {
                    border-right: 0;
                    border-left: 0;
                }

                &:first-child {
                    border-right: none;
                }

                &:last-child {
                    border-left: none;
                }
            }

            .form-control {
                border-color: ${props => props.theme['$input-focus-border-color']};
            }
        }

        /* input is not empty */
        &.input-group-filled:not(.input-group-focused) {
            .input-group-addon {
                border: ${props => props.theme['$input-border-width']} solid
                    ${props => props.theme['$strong-gray']};

                &:not(:first-child):not(:last-child) {
                    border-right: 0;
                    border-left: 0;
                }

                &:first-child {
                    border-right: none;
                }

                &:last-child {
                    border-left: none;
                }
            }

            .form-control {
                border-color: ${props => props.theme['$strong-gray']};
            }
        }

        .form-control {
            &:not(:first-child):not(:last-child) {
                border-right: 0;
                border-left: 0;
                padding-right: 0;
                padding-left: 0;
            }

            &:first-child {
                border-right: 0;
                padding-right: 0;
            }

            &:last-child {
                border-left: none;
                padding-left: 0;
            }
        }
    }
`

export default InputGroup
